import React, { useMemo } from 'react';
import { Form, Select, Space, Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Service } from '@/api/mcp-appointments';

const { Option } = Select;
const { Text } = Typography;

interface ServiceSelectorProps {
  services: Service[] | undefined;
  loading: boolean;
  onChange?: (serviceId: string) => void;
}

export const ServiceSelector: React.FC<ServiceSelectorProps> = ({
  services,
  loading,
  onChange
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  const options = useMemo(() => {
    if (!services) {
      return [];
    }
    return services.map((service) => ({
      value: service.id,
      label: service.name,
      durationMinutes: service.durationMinutes,
      confirmationMode: service.confirmationMode
    }));
  }, [services]);

  return (
    <Form.Item
      label={t('appointmentComponents.create.service.label')}
      name="serviceId"
      rules={[{ required: true, message: t('appointmentComponents.create.service.required') }]}
    >
      <Select
        placeholder={t('appointmentComponents.create.service.placeholder')}
        size="middle"
        loading={loading}
        onChange={onChange}
        showSearch
        optionFilterProp="label"
        filterOption={(input, option) => {
          const label = option?.label;
          if (typeof label === 'string') {
            return label.toLowerCase().includes(input.toLowerCase());
          }
          return String(option?.value ?? '').toLowerCase().includes(input.toLowerCase());
        }}
      >
        {options.map((option) => (
          <Option key={option.value} value={option.value} label={option.label}>
            {/* Name truncates so the duration and confirmation tag stay visible on narrow screens */}
            <div style={{ display: 'flex', alignItems: 'center', gap: token.marginXS, minWidth: 0 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.label}
              </span>
              <Space size={4} style={{ flexShrink: 0 }}>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {t('appointmentComponents.create.service.durationTag', { count: option.durationMinutes })}
                </Text>
                <Tag
                  color={option.confirmationMode === 'DIRECT' ? 'green' : 'gold'}
                  style={{ margin: 0, fontSize: token.fontSizeSM }}
                >
                  {option.confirmationMode === 'DIRECT'
                    ? t('appointmentComponents.create.service.confirmationDirect')
                    : t('appointmentComponents.create.service.confirmationNeedsApproval')}
                </Tag>
              </Space>
            </div>
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};
